import {
  createSettingsParams,
  ISettingsParam,
  PartialBy,
  SettingsParamType,
} from '@wix/tpa-settings';
import { AlignmentOptions, SlotsAvailability } from '../../types/types';
import { AccessibilityHtmlTags } from '../../utils/a11y/consts';
import { LayoutTypes } from './viewModel/agendaViewModel/agendaViewModel';
import {
  Metadata,
  MetadataValue,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';

export const withoutDefaultFallback = (settingsParam: ISettingsParam) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});
export type ISettingsParams = {
  timeSlotsDisplay: SlotsAvailability;
  limitTimeSlots: SettingsParamType.Boolean;
  showLoadMoreButton: SettingsParamType.Boolean;
  maxTimeSlotsPerDay: SettingsParamType.Number;
  scrollBetweenDays: SettingsParamType.Boolean;
  itemsBeforeLoadMore: SettingsParamType.Number;
  selectedLocations: SettingsParamType.Text[];
  selectedCategories: SettingsParamType.Text[];
  titleText: SettingsParamType.Text;
  titleTagName: SettingsParamType.String;
  dateTagName: SettingsParamType.String;
  headerFiltersTagName: SettingsParamType.String;
  tableDatesTagName: SettingsParamType.String;
  sessionsItemsServiceNameTag: SettingsParamType.String;
  sessionItemsRegularButtonNameTag: SettingsParamType.String;
  sessionsItemsPrimaryNameTag: SettingsParamType.String;
  loadMoreButtonNameTag: SettingsParamType.String;
  sessionsItemsSecondaryNameTag: SettingsParamType.String;
  tableDayTagName: SettingsParamType.String;
  selectedServices: SettingsParamType.String[];
  timeSlotsPerDay: SettingsParamType.Number;
  spotsLeftVisibility: SettingsParamType.Boolean;
  noSpotsLeft: SettingsParamType.Text;
  spotsLeftFormat: SettingsParamType.Text;
  serviceLabel: SettingsParamType.Text;
  staffMemberLabel: SettingsParamType.Text;
  locationLabel: SettingsParamType.Text;
  timeSlotsBeforeLoadMore: SettingsParamType.Number;
  toEarlyToBookIndication: SettingsParamType.Text;
  toLateToBookIndication: SettingsParamType.Text;
  bookOnlineDisabledIndication: SettingsParamType.Text;
  joinWaitlistButton: SettingsParamType.Text;
  pendingApprovalButton: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  loadMoreButton: SettingsParamType.Text;
  alreadyBookedButton: SettingsParamType.Text;
  blockedDayText: SettingsParamType.Text;
  layoutStyle: SettingsParamType.String;
  sessionItemsAlignment: SettingsParamType.String;
};

export type ISettingsParamsDefinitions = {
  [key in keyof ISettingsParams]: PartialBy<
    ISettingsParam<ISettingsParams[key]>,
    'key'
  >;
};
const getMetadata = <T extends MetadataValue>(
  defaultValue: T,
): Metadata<T> => ({
  type: StyleType.UNKNOWN_CssPropertyType,
  defaultValue: {
    type: 'static',
    data: defaultValue,
  },
});

const settingsParam = <T extends MetadataValue>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(defaultValue),
});

const typedSettingsParam = <
  Type extends SettingsParamType,
  T extends MetadataValue,
>(
  type: Type | undefined,
  defaultValue: T,
) => ({
  type,
  ...settingsParam(defaultValue),
});

export const settingsParamsDefinitions: ISettingsParamsDefinitions = {
  timeSlotsDisplay: {
    getDefaultValue: () => SlotsAvailability.ALL,
  },
  showLoadMoreButton: {
    getDefaultValue: () => true,
  },
  limitTimeSlots: {
    getDefaultValue: () => true,
  },
  maxTimeSlotsPerDay: {
    getDefaultValue: () => 6,
  },
  scrollBetweenDays: {
    getDefaultValue: () => true,
  },
  itemsBeforeLoadMore: {
    getDefaultValue: () => 12,
  },
  selectedLocations: typedSettingsParam(SettingsParamType.Object, []),
  selectedCategories: {
    getDefaultValue: () => [],
  },
  selectedServices: {
    getDefaultValue: () => [],
  },
  timeSlotsPerDay: {
    getDefaultValue: () => 10,
  },
  titleText: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.title.placeholder'),
  },
  titleTagName: {
    getDefaultValue: () => AccessibilityHtmlTags.SecondaryHeading,
  },
  dateTagName: {
    getDefaultValue: () => AccessibilityHtmlTags.SecondaryHeading,
  },
  headerFiltersTagName: {
    getDefaultValue: () => AccessibilityHtmlTags.SecondaryHeading,
  },
  tableDatesTagName: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  tableDayTagName: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  sessionItemsRegularButtonNameTag: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  loadMoreButtonNameTag: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  spotsLeftVisibility: {
    getDefaultValue: () => true,
  },
  sessionsItemsServiceNameTag: {
    getDefaultValue: () => AccessibilityHtmlTags.MainHeading,
  },
  sessionsItemsPrimaryNameTag: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  sessionsItemsSecondaryNameTag: {
    getDefaultValue: () => AccessibilityHtmlTags.Paragraph,
  },
  noSpotsLeft: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.no-availability-text.placeholder'),
  },
  spotsLeftFormat: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.table-slots.availability.placeholder'),
  },

  serviceLabel: {
    getDefaultValue: ({ t }) =>
      t(
        'daily-agenda.settings.text.filters-and-preferences.service-label.placeholder',
      ),
  },
  staffMemberLabel: {
    getDefaultValue: ({ t }) =>
      t(
        'daily-agenda.settings.text.filters-and-preferences.staff-member-label.placeholder',
      ),
  },
  locationLabel: {
    getDefaultValue: ({ t }) =>
      t(
        'daily-agenda.settings.text.filters-and-preferences.location-label.placeholder',
      ),
  },
  layoutStyle: {
    type: SettingsParamType.String,
    getDefaultValue: () => LayoutTypes.LIST,
  },
  sessionItemsAlignment: {
    type: SettingsParamType.String,
    getDefaultValue: () => AlignmentOptions.TOP,
  },
  timeSlotsBeforeLoadMore: {
    getDefaultValue: () => 12,
  },
  toEarlyToBookIndication: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.to-early-to-book.placeholder'),
  },
  toLateToBookIndication: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.to-late-to-book.placeholder'),
  },
  bookOnlineDisabledIndication: {
    getDefaultValue: ({ t }) => t('app.settings.defaults.button.more-info'),
  },
  joinWaitlistButton: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.wait-list.placeholder'),
  },
  pendingApprovalButton: {
    getDefaultValue: ({ t }) =>
      t('app.settings.defaults.booking-details.pending-approval.text'),
  },
  bookButtonText: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.book-button.placeholder'),
  },
  loadMoreButton: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.load-more-tome-slots.placeholder'),
  },
  alreadyBookedButton: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.button.already-booked.placeholder'),
  },
  blockedDayText: {
    getDefaultValue: ({ t }) =>
      t('daily-agenda.settings.text.table-slots.blocked-day.placeholder'),
  },
};

export default createSettingsParams<ISettingsParams>(settingsParamsDefinitions);
